import { useEffect } from 'react';

import { useQueryState } from 'nuqs';

import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { QueryStringKey } from '@lib/url/queryUtils';

import { openPostViewerEventSources } from '../../components/PostsViewer/types/openPostViewerEventSources';

const isOpenPostViewerEventSource = (value: string): value is openPostViewerEventSources =>
  Object.values<string>(openPostViewerEventSources).includes(value);

interface IPostViewer {
  currentPostViewerId: string | undefined;
  closePostViewer: () => Promise<void>;
  isPostViewerOpen: boolean;
  openPostViewer: (postId?: string, eventSource?: openPostViewerEventSources) => Promise<void>;
  openPostViewerEventSource: openPostViewerEventSources | null;
  setCurrentPostViewerId: (postId: string) => Promise<void>;
}

export const usePostViewer = (): IPostViewer => {
  const clientHasRendered = useClientHasRendered();
  const [isOpen, setIsOpen] = useQueryState(QueryStringKey.PostViewerOpen, { history: 'push' });
  const [postId, setPostId] = useQueryState(QueryStringKey.CurrentPostViewerId);
  const [openPostViewerEventSource, setOpenPostViewerEventSource] = useQueryState(
    QueryStringKey.PostViewerEventSource,
    {
      parse: (query: string) => (isOpenPostViewerEventSource(query) ? query : null),
    },
  );
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const openPostViewer = async (openPostId?: string, eventSource?: openPostViewerEventSources) => {
    if (eventSource) {
      await setOpenPostViewerEventSource(eventSource, { scroll: false, shallow: true });
    }
    if (openPostId) {
      await setPostId(openPostId, { scroll: false, shallow: true });
    }
    await setIsOpen('true', { scroll: false, shallow: true });
  };

  const closePostViewer = async (): Promise<void> => {
    await setIsOpen(null, { scroll: false, shallow: true });
    await setPostId(null, { scroll: false, shallow: true });
    await setOpenPostViewerEventSource(null, { scroll: false, shallow: true });
  };

  const setCurrentPostViewerId = async (postId: string): Promise<void> => {
    await setPostId(postId, { scroll: false, shallow: true });
  };

  return {
    currentPostViewerId: postId ?? undefined,
    closePostViewer,
    isPostViewerOpen: !clientHasRendered ? false : isOpen === 'true',
    openPostViewer,
    openPostViewerEventSource,
    setCurrentPostViewerId,
  };
};
